import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  ReactNode,
} from 'react';
import { useAuth } from './AuthContext';
import { ProfileService } from '../services/profileService';
import {
  fetchCatalog,
  fetchMyTranslationAccess,
  fetchPublicLanguages,
  type I18nAccess,
} from '../services/i18nService';
import {
  applyPreviewLink,
  applyRemoteLanguages,
  DEFAULT_LANGUAGE,
  getFallbackChain,
  getLanguage,
  getPublishedLanguages,
  getSelectableLanguages,
  isLanguageSelectable,
  negotiateBrowserLanguage,
  setPreviewLanguages,
} from '../i18n/languages';
import {
  dismissLanguageOffer,
  getStoredVisitorLanguage,
  hasExplicitLanguageChoice,
  isLanguageOfferDismissed,
  resolveInitialLocale,
  setStoredVisitorLanguage,
} from '../i18n/localeStorage';
import {
  setActiveLocale,
  setRemoteCatalog,
  t,
  tStepTemplate,
  type TranslateVars,
} from '../i18n/translate';

interface LocaleContextType {
  locale: string;
  setLocale: (code: string, opts?: { persistProfile?: boolean }) => Promise<void>;
  t: (key: string, vars?: TranslateVars) => string;
  tStep: (templateKey: string, stepNumber: number) => string;
  selectableLanguages: ReturnType<typeof getSelectableLanguages>;
  offerLanguage: string | null;
  acceptOffer: () => Promise<void>;
  declineOffer: () => void;
  /** Language picked automatically from the browser for a new signed-out visitor. */
  autoLanguage: string | null;
  keepAutoLanguage: () => void;
  revertAutoLanguage: () => void;
  /** Bumps when the language registry or a server catalog changes. */
  i18nVersion: number;
  /** Translation Admin access for the signed-in user (null when signed out / none). */
  translationAccess: I18nAccess | null;
}

const LocaleContext = createContext<LocaleContextType | undefined>(undefined);

export const LocaleProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user, isLoading: authLoading } = useAuth();
  // Sync init from cookie/storage so first paint matches stored choice (avoids EN flash + frozen EN action copy).
  const [locale, setLocaleState] = useState<string>(() => {
    const param =
      typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('preview') : null;
    if (param !== null) {
      setStoredVisitorLanguage(applyPreviewLink(param) ?? DEFAULT_LANGUAGE);
    }
    const stored = resolveInitialLocale(null);
    const initial = isLanguageSelectable(stored) ? stored : DEFAULT_LANGUAGE;
    setActiveLocale(initial);
    return initial;
  });
  const [offerLanguage, setOfferLanguage] = useState<string | null>(null);
  const [autoLanguage, setAutoLanguage] = useState<string | null>(null);
  const [registryReady, setRegistryReady] = useState(false);
  const [profileSynced, setProfileSynced] = useState(false);
  const [i18nVersion, setI18nVersion] = useState(0);
  const [translationAccess, setTranslationAccess] = useState<I18nAccess | null>(null);
  const localeRef = useRef(locale);
  localeRef.current = locale;

  const bump = useCallback(() => setI18nVersion((v) => v + 1), []);

  const selectableLanguages = useMemo(
    () => getSelectableLanguages(),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- registry is module state; version tracks it
    [locale, i18nVersion]
  );

  const applyLocale = useCallback((code: string) => {
    const next = isLanguageSelectable(code) ? code : DEFAULT_LANGUAGE;
    setActiveLocale(next);
    setLocaleState(next);
    if (typeof document !== 'undefined') {
      document.documentElement.lang = next;
    }
  }, []);

  /** After the registry or preview access changes, keep the locale valid. */
  const revalidateLocale = useCallback(() => {
    const current = localeRef.current;
    if (current !== DEFAULT_LANGUAGE && !isLanguageSelectable(current)) {
      applyLocale(DEFAULT_LANGUAGE);
      return;
    }
    if (current === DEFAULT_LANGUAGE && hasExplicitLanguageChoice()) {
      const stored = getStoredVisitorLanguage();
      if (stored && stored !== DEFAULT_LANGUAGE) applyLocale(stored);
    }
  }, [applyLocale]);

  /** Published non-English language the browser prefers, if any. */
  const browserLanguage = useCallback((): string | null => {
    const browser =
      typeof navigator !== 'undefined' ? navigator.languages || [navigator.language] : ['en'];
    const negotiated = negotiateBrowserLanguage(browser);
    const match = getPublishedLanguages().find((l) => l.code === negotiated && l.code !== 'en');
    return match ? match.code : null;
  }, []);

  const computeOffer = useCallback(() => {
    if (hasExplicitLanguageChoice() || isLanguageOfferDismissed()) return;
    setOfferLanguage(browserLanguage());
  }, [browserLanguage]);

  const setLocale = useCallback(
    async (code: string, opts?: { persistProfile?: boolean }) => {
      const next = isLanguageSelectable(code) ? code : DEFAULT_LANGUAGE;
      applyLocale(next);
      setStoredVisitorLanguage(next);
      dismissLanguageOffer();
      setOfferLanguage(null);
      setAutoLanguage(null);

      const persist = opts?.persistProfile !== false && !!user;
      if (persist) {
        try {
          await ProfileService.updateUserProfile({ preferred_language: next });
        } catch (e) {
          console.error('Failed to save preferred_language', e);
        }
      }
    },
    [applyLocale, user]
  );

  // Server language registry (published status lives there, not in the bundle).
  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.lang = locale;
    }
    computeOffer();
    let cancelled = false;
    (async () => {
      const remote = await fetchPublicLanguages();
      if (cancelled) return;
      if (remote !== null) {
        applyRemoteLanguages(remote);
        revalidateLocale();
        computeOffer();
        bump();
      }
      setRegistryReady(true);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount
  }, []);

  // New signed-out visitor: use the browser's language when it's published.
  // Signed-in users keep their profile language and get the offer banner instead.
  useEffect(() => {
    if (!registryReady || authLoading || user) return;
    if (hasExplicitLanguageChoice() || isLanguageOfferDismissed()) return;
    const code = browserLanguage();
    if (!code) return;
    applyLocale(code);
    setStoredVisitorLanguage(code);
    setAutoLanguage(code);
  }, [registryReady, authLoading, user, browserLanguage, applyLocale]);

  // Server translations for the active locale (and its non-English fallbacks).
  useEffect(() => {
    if (locale === DEFAULT_LANGUAGE) return;
    let cancelled = false;
    const codes = getFallbackChain(locale).filter((c) => c !== DEFAULT_LANGUAGE);
    (async () => {
      const results = await Promise.all(codes.map((c) => fetchCatalog(c)));
      if (cancelled) return;
      let changed = false;
      results.forEach((messages, i) => {
        if (messages && Object.keys(messages).length > 0) {
          setRemoteCatalog(codes[i], messages);
          changed = true;
        }
      });
      if (changed) bump();
    })();
    return () => {
      cancelled = true;
    };
  }, [locale, bump]);

  // Signed in: load translation access (preview languages) first, then profile language.
  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setProfileSynced(false);
      setTranslationAccess(null);
      setPreviewLanguages(null);
      revalidateLocale();
      bump();
      return;
    }
    let cancelled = false;
    (async () => {
      const access = await fetchMyTranslationAccess();
      if (cancelled) return;
      setTranslationAccess(access && (access.is_manager || access.languages.length > 0) ? access : null);
      setPreviewLanguages(access?.languages ?? null);
      bump();

      const linked = applyPreviewLink(null);
      if (linked && localeRef.current === linked) {
        // Opened with a ?preview= link: keep that language instead of the profile's.
        setProfileSynced(true);
        return;
      }

      try {
        const profile = await ProfileService.getUserProfile();
        if (cancelled) return;
        const preferred = profile.preferred_language;
        if (!preferred) {
          // No saved language yet: keep the one they're using and remember it.
          await ProfileService.updateUserProfile({ preferred_language: localeRef.current });
        } else if (isLanguageSelectable(preferred)) {
          applyLocale(preferred);
          setStoredVisitorLanguage(preferred);
        } else if (!getLanguage(preferred)) {
          // Unknown code on profile: replace with the visitor's current choice.
          const visitor = resolveInitialLocale(null);
          await ProfileService.updateUserProfile({ preferred_language: visitor });
          applyLocale(visitor);
        } else {
          // Known but not available to this user right now (e.g. unpublished): keep the profile value.
          revalidateLocale();
        }
        setProfileSynced(true);
      } catch (e) {
        console.error('Locale profile sync failed', e);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync once per login
  }, [user?.email, authLoading, applyLocale]);

  const acceptOffer = useCallback(async () => {
    if (!offerLanguage) return;
    await setLocale(offerLanguage);
  }, [offerLanguage, setLocale]);

  const declineOffer = useCallback(() => {
    dismissLanguageOffer();
    setOfferLanguage(null);
    setStoredVisitorLanguage(DEFAULT_LANGUAGE);
    applyLocale(DEFAULT_LANGUAGE);
  }, [applyLocale]);

  const keepAutoLanguage = useCallback(() => setAutoLanguage(null), []);

  const revertAutoLanguage = useCallback(() => {
    void setLocale(DEFAULT_LANGUAGE, { persistProfile: false });
  }, [setLocale]);

  const translate = useCallback(
    (key: string, vars?: TranslateVars) => t(key, vars),
    // locale + i18nVersion in deps so consumers re-render with fresh strings
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [locale, i18nVersion]
  );

  const tStep = useCallback(
    (templateKey: string, stepNumber: number) => tStepTemplate(templateKey, stepNumber),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [locale, i18nVersion]
  );

  const value = useMemo(
    () => ({
      locale,
      setLocale,
      t: translate,
      tStep,
      selectableLanguages,
      offerLanguage: user && profileSynced ? offerLanguage : null,
      acceptOffer,
      declineOffer,
      autoLanguage: user ? null : autoLanguage,
      keepAutoLanguage,
      revertAutoLanguage,
      i18nVersion,
      translationAccess,
    }),
    [
      locale,
      setLocale,
      translate,
      tStep,
      selectableLanguages,
      offerLanguage,
      acceptOffer,
      declineOffer,
      autoLanguage,
      keepAutoLanguage,
      revertAutoLanguage,
      profileSynced,
      user,
      i18nVersion,
      translationAccess,
    ]
  );

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
};

export function useLocale(): LocaleContextType {
  const ctx = useContext(LocaleContext);
  if (!ctx) {
    throw new Error('useLocale must be used within a LocaleProvider');
  }
  return ctx;
}

export function useT() {
  return useLocale().t;
}

/** Safe for modules that may render outside provider during tests — falls back to t(). */
export function getLanguageDisplayName(code: string): string {
  return getLanguage(code)?.nativeName || getLanguage(code)?.name || code;
}
